/**
 * EditorTabBar — VS Code-style open-file tabs for Fix & Continue.
 *
 * single-click in Explorer → preview tab (italic filename, one at a time)
 * double-click in Explorer → permanent tab (normal weight, stays open)
 * ● button on tab → file has unsaved changes; click to discard & close
 * × button on tab → close (with discard confirm if dirty)
 */
import React, { useRef, useEffect } from 'react'
import './EditorTabBar.css'

export default function EditorTabBar({
  tabs,           // [{ path, filename, isPreview }]
  activeTabPath,
  dirtyPaths,     // Set<string> of paths with unsaved changes
  onActivate,     // (path) => void
  onClose,        // (path) => void
}) {
  const barRef = useRef(null)

  // Scroll the active tab into view whenever it changes
  useEffect(() => {
    if (!barRef.current || !activeTabPath) return
    const el = barRef.current.querySelector('.etb-tab.active')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }, [activeTabPath])

  if (tabs.length === 0) {
    return (
      <div className="etb-root etb-empty">
        <span className="etb-empty-hint">Select a file from the explorer to start editing</span>
      </div>
    )
  }

  return (
    <div className="etb-root" ref={barRef}>
      {tabs.map(tab => {
        const isActive = tab.path === activeTabPath
        const isDirty = dirtyPaths.has(tab.path)
        return (
          <div
            key={tab.path}
            className={`etb-tab${isActive ? ' active' : ''}${tab.isPreview ? ' preview' : ''}`}
            onClick={() => onActivate(tab.path)}
            title={tab.path}
          >
            <span className="etb-tab-name">{tab.filename}</span>
            <button
              className={`etb-close${isDirty ? ' etb-close--dirty' : ''}`}
              onClick={e => { e.stopPropagation(); onClose(tab.path) }}
              title={isDirty ? 'Unsaved changes — click to discard and close' : 'Close tab'}
            >
              {isDirty ? '●' : '×'}
            </button>
          </div>
        )
      })}
    </div>
  )
}
